import {
  AnalyticsProps,
  NOT_TRACKED_CONTENT,
} from '../../InternalLibrary/analytics';
import {
  default as Checkbox,
  CheckboxProps,
} from 'components/atoms/Checkbox/Checkbox';
import {
  default as FieldGroup,
  FieldGroupProps,
} from 'components/atoms/FieldGroup/FieldGroup';
import { HTMLString } from '../../types/HTMLString';
import { ReactElement } from 'react';
import { useAnalytics } from 'use-analytics';
import { useCallback } from 'react';
import { useFormik } from 'formik';
import { useValidationText } from 'hooks/useValidationText';

// TODO: make the texts in the FieldGroup padded from the left same as checkbox label

export interface CheckboxActorProps extends AnalyticsProps {
  formik: ReturnType<typeof useFormik<any>>;
  name: string;
  text?: HTMLString | ReactElement;
  textHTML?: HTMLString;
  fieldGroupProps?: Omit<FieldGroupProps, 'children'>;
  checkboxProps?: Partial<CheckboxProps>;
  dataTest?: string;
  className?: string;
  labelClassName?: string;
}

export const CheckboxActor: React.FC<CheckboxActorProps> = ({
  formik,
  name,
  text,
  textHTML,
  fieldGroupProps = {},
  checkboxProps = {},
  analytics,
  dataTest,
  className,
  labelClassName,
}) => {
  const { track } = useAnalytics();

  const onChange = useCallback(
    (e: any) => {
      formik.status?.errors?.[name] && delete formik.status.errors[name];
      formik.handleChange(e);

      if (analytics?.dontTrack) {
        return;
      }

      track('checkbox', {
        description: analytics?.description,
        checkboxChecked: e.target.checked,
        content: analytics?.dontTrackContent
          ? NOT_TRACKED_CONTENT
          : textHTML || text,
      });
    },
    [
      analytics?.description,
      analytics?.dontTrack,
      analytics?.dontTrackContent,
      formik,
      name,
      text,
      textHTML,
      track,
    ],
  );

  const getFormikValue = (
    values: Record<string, unknown> | undefined,
    name: string,
  ): boolean => {
    if (!values) return false;
    if (!name.includes('.')) return !!values[name];
    const [firstPart, restName] = name.split('.');
    const nextValues = formik.values[firstPart];
    return getFormikValue(nextValues, restName);
  };

  return (
    <FieldGroup
      validationText={useValidationText(formik, name)}
      {...fieldGroupProps}
    >
      <div>
        <Checkbox
          {...checkboxProps}
          className={className}
          labelClassName={labelClassName}
          text={text}
          textHTML={textHTML}
          onChange={onChange}
          name={name}
          value={getFormikValue(formik.values, name)}
          errors={useValidationText(formik, name) !== undefined}
          dataTest={dataTest}
        />
      </div>
    </FieldGroup>
  );
};
